import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EInvoiceControllerService } from '@app/api/einvoice-service/api/eInvoiceController.service';
import { CommonInvoiceResponseDTO } from '@app/api/einvoice-service/model/commonInvoiceResponseDTO';
import { InvoiceFilterRequest } from '@app/api/einvoice-service/model/invoiceFilterRequest';
import { GetPDFRequestDTO } from '@app/api/einvoice-service/model/getPDFRequestDTO';

export interface EInvoiceHistory {
  id: number;
  invoiceSymbol: string;
  invoiceTemplate: string;
  invoiceNumber: string;
  buyerName: string;
  buyerTaxCode: string;
  subtotal: number;
  taxAmount: number;
  discount: number;
  totalAmount: number;
  paymentMethod: string;
  currency: string;
  // status: 'ISSUED' | 'REPLACED' | 'ADJUSTED';
  status: string;
  taxSubmissionStatus: 'SUBMITTED' | 'PENDING' | 'FAILED';
  issuedDate: string;
  providerCode: string;
  providerName: string;
  storeName?: string;
  companyId?: number;
  companyName?: string;
  providerId?: number;
  accountId?: number;
}

export interface EInvoiceHistoryPageRequest {
  invoiceNo?: string;
  orderNo?: string;
  issuedFrom?: string;
  issuedTo?: string;
  status?: string;
  companyId?: number;
  providerId?: number;
  page: number;
  size: number;
}

export interface EInvoiceHistoryPageResponse {
  pageContent: EInvoiceHistory[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

@Injectable({
  providedIn: 'root',
})
export class EInvoiceHistoryService {
  private readonly apiService = inject(EInvoiceControllerService);

  searchInvoices(
    payload: EInvoiceHistoryPageRequest,
  ): Observable<EInvoiceHistoryPageResponse> {
    const filter: InvoiceFilterRequest = {
      providerId: payload.providerId || undefined,
      invoiceNo: payload.invoiceNo || undefined,
      orderNo: payload.orderNo || undefined,
      issuedFrom: payload.issuedFrom || undefined,
      issuedTo: payload.issuedTo || undefined,
      status: payload.status || undefined,
    };

    return this.apiService
      .getInvoices(filter, payload.page, payload.size, undefined, undefined, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((page) => ({
          pageContent: (page.pageContent || []).map((dto) =>
            this.mapDtoToHistory(dto),
          ),
          totalElements: page.totalElements ?? 0,
          totalPages: page.totalPages ?? 0,
          page: page.number ?? payload.page,
          size: page.size ?? payload.size,
        })),
      );
  }

  private mapDtoToHistory(dto: CommonInvoiceResponseDTO): EInvoiceHistory {
    return {
      id: dto.id ?? 0,
      invoiceSymbol: dto.invoiceSeries ?? '',
      invoiceTemplate: dto.templateCode ?? '',
      invoiceNumber: dto.invoiceNo ?? '',
      buyerName: dto.buyerName ?? '',
      buyerTaxCode: dto.buyerTaxCode ?? '',
      subtotal: dto.subtotalAmount ?? 0,
      taxAmount: dto.taxAmount ?? 0,
      discount: dto.discountAmount ?? 0,
      totalAmount: dto.totalAmount ?? 0,
      paymentMethod: dto.paymentMethod ?? '',
      currency: dto.currency ?? '',
      status: dto.status ?? '',
      taxSubmissionStatus: dto.sentToTaxAuthority ? 'SUBMITTED' : 'PENDING',
      issuedDate: dto.issuedAt ?? '',
      providerId: dto.providerId ?? 0,
      providerCode: String(dto.providerId ?? ''),
      storeName: dto.storeName,
      accountId: dto.accountId,
      companyId: dto.companyId ?? 0,
      companyName: dto.companyName ?? '',
      providerName: dto.providerName ?? '',
    };
  }

  downloadInvoicePdf(invoice: EInvoiceHistory): Observable<Blob> {
    const request: GetPDFRequestDTO = {
      invoiceId: invoice.id,
      accountId: invoice.accountId,
      invoiceNo: invoice.invoiceNumber,
      templateCode: invoice.invoiceTemplate,
      fileType: 'PDF',
    };

    return this.apiService
      .downloadPDF(request, 'body', false, {
        httpHeaderAccept: 'application/pdf' as any,
      })
      .pipe(
        map((response) => new Blob([response], { type: 'application/pdf' })),
      );
  }
}
